/**
 * TASKS-ALL.WIDGET
 * List all tasks for the current action plan
 */

import { store } from "@/index"
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { actionPlanActivate, actionPlanFetchTasks } from "@/redux/action-plan.actions"
import { ActionPlan, ActionPlanTask, ActionPlanTaskStatus } from "@/redux/action-plan.types"
import { useEffect, useState } from "react"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import PageLoader from "@/components/page-loader"
import { orderBy, uniqBy } from "lodash"
import { Survey, SurveyState } from "@/redux/survey.types"
import { surveyActivate, surveyFetchOne, surveyGet } from "@/redux/survey.actions"
import Card from "@/components/card"
import ListItem from "@/components/list-item"
import { faChevronDown, faChevronUp, faPencilAlt, faUserCircle } from "@fortawesome/free-solid-svg-icons"
import ListButton from "@/components/list-button"
import Space from "@/components/space"
import { topicGet } from "@/redux/topic.actions"
import { Topic, TopicState } from "@/redux/topic.types"
import TasksTopicLabelWidget from "./tasks-topic-label.widget"
import { fetchTopics } from "@/redux/_archive.actions"
import { DashboardFilters } from "@/redux/filter.types"
import { Session } from "@/redux/_session.types"
import ListIcon from "@/components/list-icon"
import Chip from "@/components/chip"
import getColor from "@/utils/get-color.utils"
import PagePlaceholder from "@/components/page-placeholder"
import AdmUserPopulationsWidget from "./adm-user-populations.widget"
import Modal from "@/components/modal"
import Dropdown from "@/components/dropdown"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { useNavigate } from "react-router-dom"
import { userActivate } from "@/redux/user.actions"
import { formatDate } from "@/utils/format-date.utils"
import { getActionPlanAccess } from "@/utils/observer-scope-options.utils"

interface StateProps extends WithTranslation {
  _session: Session
  survey: SurveyState
  topic: TopicState
}

interface OwnProps {
  exportRequested?: boolean
  onExportClose?: Function
}

type Props = StateProps & OwnProps

function TasksAllWidget(props: Props) {
  const { t } = props
  //An observer with the read only access sees the actions without the pencil
  const isEditable = props._session.userRole === "ADMIN" || getActionPlanAccess(props._session) === "scopeWrite"
  const navigate = useNavigate()

  //State
  const [isLoading, setIsLoading] = useState(true)
  const [isSurveyLoading, setIsSurveyLoading] = useState(true)
  const [tasks, setTasks] = useState<ActionPlanTask[]>([])
  const [topics, setTopics] = useState<Topic[]>([])
  const [exportSurvey, setExportSurvey] = useState<Survey | null>(null)

  useEffect(() => {
    if (props.exportRequested && !exportSurvey && props.survey.list.length > 0) {
      setExportSurvey(props.survey.active.id ? props.survey.active : props.survey.list[0])
    }
  }, [props.exportRequested, props.survey.active, props.survey.list, exportSurvey])

  //On load fetch all tasks
  useEffect(() => {
    loadData()

    async function loadData() {
      store.dispatch(surveyActivate(null))
      setIsLoading(true)

      const response: any = await store.dispatch(actionPlanFetchTasks())

      if (!response.error) {
        //Format tasks
        const tasksResponse = response.map((x) => {
          return new ActionPlanTask({
            ...x,
            ActionPlan: new ActionPlan(x.ActionPlan)
          })
        })

        setTasks(tasksResponse)

        //Get surveys
        const surveys = orderBy(
          uniqBy(
            tasksResponse.map((x) => x.ActionPlan.Survey),
            "surveyId"
          ),
          ["dateStart"],
          ["desc"]
        ) as unknown as Survey[]

        //Save surveys in the store
        store.dispatch(surveyGet(surveys, [], false))
        if (surveys.length) {
          openSurvey(surveys[0], tasksResponse)
        }
      }

      setIsLoading(false)
    }
  }, [])

  //Click task
  //open edit task modal
  async function handleClickTask(task: ActionPlanTask) {
    if (task.ActionPlan) {
      const actionPlanTasks = tasks
        .filter((x) => x.ActionPlan?.id === task.ActionPlan?.id)
        .map((x) => new ActionPlanTask(x))
      const actionPlan = new ActionPlan({ ...task.ActionPlan, tasks: actionPlanTasks })
      store.dispatch(actionPlanActivate(actionPlan))
      store.dispatch(surveyActivate(actionPlan.Survey))
      store.dispatch(userActivate(actionPlan.User))
      navigate(`/tasks/edit?taskId=${task.id}`)
    }
  }

  //Open specific survey
  //Set active and get list of topics with tasks
  async function handleOpenSurvey(survey: Survey) {
    if (survey.id === props.survey.active.id) {
      store.dispatch(surveyActivate(null))
    } else {
      openSurvey(survey, tasks)
    }
  }

  //Color for the chip
  function getTaskColor(status: ActionPlanTaskStatus) {
    switch (status) {
      case "done":
        return getColor("green")
      case "pending":
        return getColor("yellow")
      default:
        return undefined
    }
  }

  function getTasksForSurvey(survey: Survey | null) {
    if (!survey) return []
    return tasks.filter((task) => task.ActionPlan?.SurveyId === survey.id || task.ActionPlan?.Survey?.id === survey.id)
  }

  function getTaskTopicLabel(task: ActionPlanTask, exportTopics: Topic[] = topics) {
    const topic = exportTopics.find((x) => x.aid === task.topicAid)
    return topic ? topic.label : (task.topicAid ?? "")
  }

  function closeExport() {
    setExportSurvey(null)
    if (props.onExportClose) {
      props.onExportClose()
    }
  }

  async function exportTasks() {
    if (!exportSurvey) return

    let exportTopics: Topic[] = topics
    const response: any = await store.dispatch(surveyFetchOne(exportSurvey.id))
    if (!response.error && response?.template?.Topics) {
      exportTopics = response.template.Topics.map((x) => new Topic(x))
    }

    const rows = getTasksForSurvey(exportSurvey).map((task) => [
      task.ActionPlan?.User?.username ?? "",
      task.ActionPlan?.User?.email ?? "",
      task.ActionPlan?.User?.populationsObserver.map((x) => `${x.filterName} : ${x.name}`).join(", ") ?? "",
      getTaskTopicLabel(task, exportTopics),
      task.title,
      task.text,
      task.datePlanned ? formatDate(task.datePlanned, false, false) : "",
      t("tasks_list_status_" + task.status)
    ])

    const worksheet = XLSX.utils.aoa_to_sheet([
      [
        t("user"),
        "Email",
        "Populations",
        t("topic"),
        t("tasks_list_title"),
        t("tasks_list_text"),
        t("tasks_date_planned"),
        t("project_status")
      ],
      ...rows
    ])
    worksheet["!cols"] = [
      { wch: 28 },
      { wch: 32 },
      { wch: 45 },
      { wch: 40 },
      { wch: 45 },
      { wch: 70 },
      { wch: 18 },
      { wch: 18 }
    ]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, t("tasks"))
    const document: any = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
    saveAs(new Blob([document]), `tasks_${exportSurvey.name}`.toLowerCase().replaceAll(/[^a-zA-Z0-9]/g, "_") + ".xlsx")
    closeExport()
  }

  //Open specific survey
  //Set active and get list of topics with tasks
  async function openSurvey(survey: Survey, tasks: ActionPlanTask[]) {
    setIsSurveyLoading(true)
    store.dispatch(surveyActivate(survey))

    //Fetch survey
    const response: any = await store.dispatch(surveyFetchOne(survey.id))

    //Handle response
    if (!response.error && response) {
      const currentSurvey = response as Survey
      const topicAids = tasks.map((x) => x.topicAid)
      setTopics(currentSurvey.template.Topics.filter((x) => topicAids.includes(x.aid)).map((x) => new Topic(x)))

      //Fetch topics
      const topicResponse: any = await store.dispatch(
        fetchTopics(
          new DashboardFilters(),
          props._session.accountOptions.topicRepartitionCeil,
          false,
          currentSurvey.id,
          currentSurvey.dateStart
        )
      )

      //Format topic
      let topics = currentSurvey.template.Topics.filter((x) => topicAids.includes(x.aid))
      if (!topicResponse.error) {
        topics = topics.map((topic) => {
          const topicWithNote = topicResponse.find((x) => x.aid === topic.aid)
          return {
            ...topic,
            note: topicWithNote?.note
          }
        }) as Topic[]
      }

      //Update store
      const topicsWithNotes = orderBy(topics, ["withScore", "note"], ["desc", "asc"]).map((x) => new Topic(x))
      setTopics(topicsWithNotes)
      store.dispatch(topicGet(topicsWithNotes))
    } else {
      store.dispatch(topicGet([]))
    }

    setIsSurveyLoading(false)
  }

  return (
    <PageLoader forceLoading status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
      {props.exportRequested && (
        <Modal
          isCloseButtonVisible
          title={t("utils_export")}
          onClose={closeExport}
          onNext={exportTasks}
          onNextCta={t("utils_export")}
          nextDisabled={!exportSurvey || getTasksForSurvey(exportSurvey).length === 0}>
          <Dropdown
            displayField="name"
            list={props.survey.list}
            value={exportSurvey?.name}
            active={exportSurvey?.id}
            onSelect={(survey: Survey) => setExportSurvey(survey)}
            title={t("tasks_survey")}
          />
          <div className="height-20" />
          <div className="grey-t">
            {exportSurvey
              ? `${getTasksForSurvey(exportSurvey).length} ${t("tasks_list").toLowerCase()}`
              : t("no_survey_active")}
          </div>
          <div className="h-100" />
        </Modal>
      )}
      {tasks.length === 0 && <PagePlaceholder title={t("tasks_actions_all_empty")} image="edit" />}
      {props.survey.list.map((survey) => {
        const isActive = survey.id === props.survey.active.id
        return (
          <Card key={survey.id} isWithoutPadding className="min-w-[800px]">
            <ListItem isEditable onClick={() => handleOpenSurvey(survey)}>
              <div className="text-lg">{survey.name}</div>
              <Space />
              <div className="mt-[3px]">
                <ListButton icon={isActive ? faChevronUp : faChevronDown} onClick={() => handleOpenSurvey(survey)} />
              </div>
            </ListItem>
            {isActive && (
              <PageLoader forceLoading status={isSurveyLoading ? STATUS_LOADING : STATUS_LOADED}>
                {topics.map((topic) => (
                  <Card key={topic.id} isWithoutPadding className="mx-[30px]">
                    <ListItem>
                      <TasksTopicLabelWidget withNote currentTopic={topic} />
                    </ListItem>
                    {tasks
                      .filter((x) => x.topicAid === topic.aid)
                      .map((task) => (
                        <ListItem key={task.id}>
                          <ListIcon fallbackIcon={faUserCircle} image={task.ActionPlan?.User?.imageUrl} />
                          <div className="w-[180px]">
                            <div className="mb-2">{task.ActionPlan?.User.username}</div>
                            {task.ActionPlan?.User && <AdmUserPopulationsWidget currentUser={task.ActionPlan?.User} />}
                          </div>
                          <div className="flex-1 grey-t">
                            <div>{task.title}</div>
                            <div className="text-sm mt-2">{task.text}</div>
                          </div>
                          <div className="min-w-20" />
                          <div className="w-[140px] flex flex-col items-end">
                            <Chip color={getTaskColor(task.status)}>{t("tasks_list_status_" + task.status)}</Chip>
                            {!task.isDone && task.datePlanned && (
                              <div className="text-sm grey-t mt-2">
                                {t("tasks_date_planned_on")} <b>{formatDate(task.datePlanned, false, false)}</b>
                              </div>
                            )}
                          </div>
                          {isEditable && <ListButton icon={faPencilAlt} onClick={() => handleClickTask(task)} />}
                        </ListItem>
                      ))}
                  </Card>
                ))}
              </PageLoader>
            )}
          </Card>
        )
      })}
    </PageLoader>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  survey: state.survey,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(TasksAllWidget))
